import type { MagiMethod } from "../config";
import type { EffectiveLimits } from "../types";
import { METHODS_BY_ID } from "../methods";
import { LanguageSelector } from "./LanguageSelector";

export type AdvancedValues = {
  deliberative: boolean;
  voteThreshold: number;
  allowAbstain: boolean;
  systemPrompt: string;
  rapporteurPrompt: string;
  language: string;
};

export function AdvancedOptions({
  method,
  values,
  onChange,
  disabled,
  defaultOpen,
  limits,
  suggestedLanguages,
}: {
  method: MagiMethod;
  values: AdvancedValues;
  onChange: (v: AdvancedValues) => void;
  disabled?: boolean;
  defaultOpen?: boolean;
  limits: EffectiveLimits;
  suggestedLanguages: string[];
}) {
  const spec = METHODS_BY_ID[method];

  function patch<K extends keyof AdvancedValues>(k: K, v: AdvancedValues[K]) {
    onChange({ ...values, [k]: v });
  }

  const rapporteurMax = limits.maxRapporteurPromptLength;
  const systemMax = limits.maxSystemPromptLength;
  const rapporteurOver = values.rapporteurPrompt.length > rapporteurMax;
  const systemOver = values.systemPrompt.length > systemMax;

  return (
    <details className="accordion" open={defaultOpen}>
      <summary>
        <span>Advanced options</span>
        <span className="muted" style={{ fontSize: 10, letterSpacing: "0.14em" }}>
          {values.deliberative ? "DELIBERATIVE · ON" : "DELIBERATIVE · OFF"}
        </span>
      </summary>
      <div className="body">
        <label className="switch">
          <input
            type="checkbox"
            checked={values.deliberative}
            onChange={(e) => patch("deliberative", e.target.checked)}
            disabled={disabled}
            data-testid="opt-deliberative"
          />
          <span className="track" />
          Deliberative (two rounds with anonymous peer review)
        </label>

        {spec?.usesAbstain && (
          <label className="switch">
            <input
              type="checkbox"
              checked={values.allowAbstain}
              onChange={(e) => patch("allowAbstain", e.target.checked)}
              disabled={disabled}
              data-testid="opt-abstain"
            />
            <span className="track" />
            Allow models to abstain
          </label>
        )}

        {spec?.usesThreshold && (
          <div className="field">
            <label htmlFor="opt-threshold">Vote threshold</label>
            <input
              id="opt-threshold"
              type="number"
              min={0}
              max={1}
              step={0.05}
              value={values.voteThreshold}
              onChange={(e) =>
                patch("voteThreshold", Number(e.target.value) || 0)
              }
              disabled={disabled}
              data-testid="opt-threshold"
            />
            <span className="muted" style={{ fontSize: 11 }}>
              Fraction of votes required for a winner (0–1).
            </span>
          </div>
        )}

        <LanguageSelector
          value={values.language}
          onChange={(v) => patch("language", v)}
          suggestions={suggestedLanguages}
          maxLength={limits.maxLanguageLength}
          disabled={disabled}
        />

        <div className="field" style={{ gridColumn: "1 / -1" }}>
          <label htmlFor="opt-rapporteur">Rapporteur prompt (optional)</label>
          <textarea
            id="opt-rapporteur"
            value={values.rapporteurPrompt}
            onChange={(e) => patch("rapporteurPrompt", e.target.value)}
            placeholder="Extra instructions for the rapporteur that writes the final summary…"
            disabled={disabled}
            data-testid="opt-rapporteur-prompt"
          />
          <CharCounter
            count={values.rapporteurPrompt.length}
            max={rapporteurMax}
            over={rapporteurOver}
          />
        </div>

        <div className="field" style={{ gridColumn: "1 / -1" }}>
          <label htmlFor="opt-system">System prompt (optional)</label>
          <textarea
            id="opt-system"
            value={values.systemPrompt}
            onChange={(e) => patch("systemPrompt", e.target.value)}
            placeholder="Context prepended to every agent prompt…"
            disabled={disabled}
            data-testid="opt-system-prompt"
          />
          <CharCounter
            count={values.systemPrompt.length}
            max={systemMax}
            over={systemOver}
          />
        </div>
      </div>
    </details>
  );
}

function CharCounter({
  count,
  max,
  over,
}: {
  count: number;
  max: number;
  over: boolean;
}) {
  return (
    <div
      className="row"
      style={{ justifyContent: "flex-end", marginTop: -2 }}
    >
      <span
        style={{
          fontSize: 12,
          color: over ? "var(--negative)" : "var(--text-dim)",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {count} / {max}
      </span>
    </div>
  );
}
